import React, { useState } from 'react';
import { useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IntlMessages from '../utils/IntlMessages';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import axios from 'axios'; // Import axios
import { translator } from "../utils/Translator";
import Swal from 'sweetalert2';
import PalyStoreLogo from '../assets/images/playstore_logo.png';
import AppStoreLogo from '../assets/images/appstore_logo.png';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const RegisterTrial = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const { translate } = useSelector(({ i18n }) => i18n);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [salesOrganization, setSalesOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [lineId, setLineId] = useState('');

  // New state variables for validation errors
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [salesOrganizationError, setSalesOrganizationError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');

  // State for loading and messages
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState([]); // New state for overall form errors

  const validateForm = () => {
    let currentErrors = [];

    // Reset all errors
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setSalesOrganizationError('');
    setPhoneNumberError('');

    if (!firstName.trim()) {
      setFirstNameError(translator(translate.ISA_PleaseEnterFirstName, 'กรุณากรอกชื่อจริง'));
      currentErrors.push(translator(translate.ISA_FirstName, 'ชื่อจริง'));
    }
    if (!lastName.trim()) {
      setLastNameError(translator(translate.ISA_PleaseEnterLastName, 'กรุณากรอกนามสกุล'));
      currentErrors.push(translator(translate.ISA_Surname, 'นามสกุล'));
    }
    if (!email.trim()) {
      setEmailError(translator(translate.ISA_PleaseEnterEmail, 'กรุณากรอกอีเมล'));
      currentErrors.push(translator(translate.ISA_Email, 'อีเมล'));
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError(translator(translate.ISA_InvalidEmailFormat, 'รูปแบบอีเมลไม่ถูกต้อง'));
      currentErrors.push(translator(translate.ISA_EmailFormat, 'รูปแบบอีเมล'));
    }
    if (!password) {
      setPasswordError(translator(translate.ISA_PleaseEnterPassword, 'กรุณากรอกรหัสผ่าน'));
      currentErrors.push(translator(translate.ISA_Password, 'รหัสผ่าน'));
    } else if (password.length < 6) { // Example: minimum 6 characters
      setPasswordError(translator(translate.ISA_PasswordMinLength, 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'));
      currentErrors.push(translator(translate.ISA_PasswordLength, 'รหัสผ่าน (ความยาว)'));
    }
    if (!confirmPassword) {
      setConfirmPasswordError(translator(translate.ISA_PleaseConfirmPassword, 'กรุณายืนยันรหัสผ่าน'));
      currentErrors.push(translator(translate.ISA_ConfirmPassword, 'ยืนยันรหัสผ่าน'));
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError(translator(translate.ISA_PasswordNotMatch, 'รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน'));
      currentErrors.push(translator(translate.ISA_PasswordMismatch, 'รหัสผ่านไม่ตรงกัน'));
    }
    if (!salesOrganization.trim()) {
      setSalesOrganizationError(translator(translate.ISA_PleaseEnterCompany, 'กรุณากรอก บริษัท/องค์กร'));
      currentErrors.push(translator(translate.ISA_CompanyOrOrganization, 'บริษัท/องค์กร'));
    }
    if (!phoneNumber.trim()) {
      setPhoneNumberError(translator(translate.ISA_PleaseEnterPhoneNumber, 'กรุณากรอกเบอร์โทรศัพท์'));
      currentErrors.push(translator(translate.ISA_PhoneNumber, 'เบอร์โทรศัพท์'));
    }

    setFormErrors(currentErrors); // Set the overall form errors
    return currentErrors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const requestBody = {
      SalesmanPassword: password,
      SalesmanName: firstName,
      SalesmanLastName: lastName,
      SalesOrganization: salesOrganization,
      Email: email,
      PhoneNumber: phoneNumber,
      LineID: lineId,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}registerTrial`, requestBody);
      const { status, message, error } = response.data;

      if (status === 'success') {
        Swal.fire({
          icon: 'success',
          title: `${translator(translate.ISA_RegisterTrialSuccess, "ลงทะเบียนใช้งาน Trial สำเร็จ")}`,
          html: `${translator(translate.ISA_RegisterTrialSuccessMsg, "ท่านสามารถเข้าใช้งาน Application iSmartsales CRM ได้ระยะเวลา 14 วัน")}<br><br>` +
                `${translator(translate.ISA_RegisterTrialDownloadApp, "สามารถคลิ๊ก download app เพื่อเริ่มทดลองการใช้งาน (เราจะส่ง Username และ Password ให้ทางอีเมล์ที่คุณลงทะเบียน)")}<br><br>` +
                `<a href="https://play.google.com/store/apps/details?id=com.ismartnewapp&hl=en" target="_blank"><img src=${PalyStoreLogo} alt="Get it on Google Play" width="150"></a>` +
                `&nbsp;&nbsp;&nbsp;` +
                `<a href="https://apps.apple.com/th/app/ismartsales/id6746042919" target="_blank"><img src=${AppStoreLogo} alt="Download on the App Store" width="150"></a>`,
          confirmButtonText: translator(translate.ISA_Button_Ok, 'ตกลง')
        });
        // Clear form fields
        setFirstName('');
        setLastName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setSalesOrganization('');
        setPhoneNumber('');
        setLineId('');
        setFormErrors([]); // Clear form errors
      } else if (status === 'warning') {
        Swal.fire({
          icon: 'warning',
          title: `${translator(translate.ISA_RegisterTrialSuccessWithNotSendEmail, "ลงทะเบียนใช้งาน Trial สำเร็จ แต่ไม่สามารถส่งอีเมลยืนยันได้!")}`,
          html: `${translator(translate.ISA_RegisterTrialSuccessMsg, "ท่านสามารถเข้าใช้งาน Application iSmartsales CRM ได้ระยะเวลา 14 วัน")}<br><br>` +
                `${translator(translate.ISA_RegisterTrialDownloadApp, "สามารถคลิ๊ก download app เพื่อเริ่มทดลองการใช้งาน (เราจะส่ง Username และ Password ให้ทางอีเมล์ที่คุณลงทะเบียน)")}<br><br>` +
                `<a href="https://play.google.com/store/apps/details?id=com.ismartnewapp&hl=en" target="_blank"><img src=${PalyStoreLogo} alt="Get it on Google Play" width="150"></a>` +
                `&nbsp;&nbsp;&nbsp;` +
                `<a href="https://apps.apple.com/th/app/ismartsales/id6746042919" target="_blank"><img src=${AppStoreLogo} alt="Download on the App Store" width="150"></a>`,
          confirmButtonText: translator(translate.ISA_Button_Ok, 'ตกลง')
        });
      } else { // status === 'fail'
        Swal.fire({
        icon: 'error',
        title: translator(translate.ISA_Error, 'เกิดข้อผิดพลาด!'),
        text: error || message || translator(translate.ISA_RegisterTrial_Fail, 'การลงทะเบียนล้มเหลว'),
        confirmButtonText: translator(translate.ISA_Button_Ok, 'ตกลง')
        });
      }

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: translator(translate.ISA_Error, 'เกิดข้อผิดพลาด!'),
        text: translator(translate.ISA_RegisterTrial_ConnectionError, 'เกิดข้อผิดพลาดในการเชื่อมต่อ: ') + (error.message || ''),
        confirmButtonText: translator(translate.ISA_Button_Ok, 'ตกลง')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-wrapper" style={{ margin: 0, padding: 0 }}>
      {/* Header */}
      <AppBar position="fixed" color="default">
        <Toolbar>
          <a 
              href="https://www.ismartsales.com"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
            >
            <img src="http://www.ismartsales.com/wp-content/uploads/2018/01/ismart.png" alt="Logo" style={{ height: 40, marginRight: 16 }} />
            
          </a>
          <Typography variant="h6" color="inherit">
              {translator(translate.ISA_RegisterPageTitle, "สมัครสมาชิกทดลองใช้")}
            </Typography>
        </Toolbar>
      </AppBar>

      {/* Form */}
      <Container 
        component="main" 
        maxWidth="sm" 
        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 0', overflowY: 'auto', maxHeight: '100vh', marginTop: isMobile ? 80 : 64 }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              {translator(translate.ISA_FillFormToRegister, "กรอกข้อมูลเพื่อสมัครสมาชิก")}
            </Typography>
          </Grid>

          

          {formErrors.length > 0 &&
            <Grid item xs={12}>
              <Box p={2} mb={2} border={1} borderColor="error.main" borderRadius={4} bgcolor="error.light">
                <Typography variant="subtitle1" color="error" gutterBottom>
                  {translator(translate.ISA_PleaseFixErrors, "กรุณาแก้ไขข้อผิดพลาดต่อไปนี้:")}
                </Typography>
                <ul>
                  {formErrors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2" color="error">
                        {error}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            </Grid>
          }

          <Grid item xs={12}>
            <form onSubmit={handleSubmit}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {translator(translate.ISA_PersonalInfo, "ข้อมูลส่วนตัว")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translator(translate.ISA_FirstName, "ชื่อจริง")}
                        fullWidth
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          setFirstNameError('');
                        }}
                        value={firstName}
                        margin="normal"
                        variant="outlined"
                        required
                        error={!!firstNameError}
                        helperText={firstNameError}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translator(translate.ISA_Surname, "นามสกุล")}
                        fullWidth
                        onChange={(e) => {
                          setLastName(e.target.value);
                          setLastNameError('');
                        }}
                        value={lastName}
                        margin="normal"
                        variant="outlined"
                        required
                        error={!!lastNameError}
                        helperText={lastNameError}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={translator(translate.ISA_CompanyOrOrganization, "บริษัท/องค์กร")}
                        fullWidth
                        onChange={(e) => {
                          setSalesOrganization(e.target.value);
                          setSalesOrganizationError('');
                        }}
                        value={salesOrganization}
                        margin="normal"
                        variant="outlined"
                        required
                        error={!!salesOrganizationError}
                        helperText={salesOrganizationError}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={translator(translate.ISA_Email, "อีเมล")}
                        type="email"
                        fullWidth
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setEmailError('');
                        }}
                        value={email}
                        margin="normal"
                        variant="outlined"
                        required
                        error={!!emailError}
                        helperText={emailError}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="password"
                        label={translator(translate.ISA_Password, "รหัสผ่าน")}
                        fullWidth
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setPasswordError('');
                        }}
                        value={password}
                        margin="normal"
                        variant="outlined"
                        required
                        error={!!passwordError}
                        helperText={passwordError}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="password"
                        label={translator(translate.ISA_ConfirmPassword, "ยืนยันรหัสผ่าน")}
                        fullWidth
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setConfirmPasswordError('');
                        }}
                        value={confirmPassword}
                        margin="normal"
                        variant="outlined"
                        required
                        error={!!confirmPasswordError}
                        helperText={confirmPasswordError}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translator(translate.ISA_PhoneNumber, "เบอร์โทรศัพท์")}
                        fullWidth
                        onChange={(e) => {
                          setPhoneNumber(e.target.value);
                          setPhoneNumberError('');
                        }}
                        value={phoneNumber}
                        margin="normal"
                        variant="outlined"
                        required
                        error={!!phoneNumberError}
                        helperText={phoneNumberError}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translate.ISA_LineID || "Line ID"}
                        fullWidth
                        onChange={(e) => {
                          setLineId(e.target.value);
                        }}
                        value={lineId}
                        margin="normal"
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  
                  <Box mt={3} display="flex" justifyContent="center">
                    <Button type="submit" variant="contained" color="primary" style={{ width: '200px' }} disabled={loading}>
                      { translator(translate.ISA_Register, "ลงทะเบียน") }
                    </Button>
                  </Box>
                </CardContent>
                
              </Card>

              
            </form>
          </Grid>
        </Grid>
      </Container>

      {loading &&
        <div className="loader-view">
          <CircularProgress />
        </div>
      }
    </div>
  );
};

export default RegisterTrial;